// Web-side registry of the workflow action *kinds* (the demodulized `actionType`).
//
// The engine has no display metadata on actions, so the FE owns the label /
// description / completion mapping. The packet-processing pair (ProcessAttachments,
// ExtractAttributes) is automated — run by the AI pipeline, no completion options;
// the rest are completed by a person. The `outputs` here mirror each action's
// declared `produces` in the seed (app/seeds/seed_data/workflows.rb); the engine
// treats those values as opaque (design §8/§11). When the engine later exposes this
// contract dynamically, this file is the single thing to drop.
import Workflow from 'models/Workflow';

export interface ActionOption {
  label: string;
  tone?: 'primary' | 'danger';
  // Exactly the `outputs` body to PATCH when this option is chosen.
  outputs: Record<string, string>;
}

export interface ActionKindMeta {
  label: string;
  // Informational only — the API returns the authoritative `categoryKey` per action.
  category: string;
  description: string;
  // Shown above the options when the action needs a person.
  prompt?: string;
  // The manual completion choices; omitted kinds render status-only.
  options?: ActionOption[];
  // The declared value whose stored content identifies the chosen option, so a
  // completed card can show the recorded result.
  resultValueName?: string;
  // Value-mediated dependencies (mirrors the seed's required/produces). The engine
  // doesn't expose these, so the FE keeps them to explain *why* a step is waiting.
  requires: string[];
  produces: string[];
}

// The shared demographic identity, produced by ExtractAttributes or entered by hand.
export const IDENTITY_VALUES = ['patient_first_name', 'patient_last_name', 'patient_gender', 'patient_dob'];

// The auto-checks and clinical review also wait on the duplicate-patient result, so they
// don't activate until identity is confirmed (and cancel if a duplicate ends the run).
const POST_IDENTITY_INPUTS = [...IDENTITY_VALUES, 'duplicate_patient_result'];

export const ACTION_KINDS: Record<string, ActionKindMeta> = {
  ProcessAttachments: {
    label: 'Process Documents',
    category: 'referral_packet',
    description: 'The AI runs each packet document through extraction.',
    requires: ['attachments'],
    produces: ['attachments_processed'],
  },
  ExtractAttributes: {
    label: 'Extract Demographics',
    category: 'referral_packet',
    description: "The AI reads the patient's demographics from the processed packet.",
    requires: ['attachments_processed'],
    produces: IDENTITY_VALUES,
  },
  DuplicatePatientSearch: {
    label: 'Duplicate Patient Search',
    category: 'referral_packet',
    description: "Confirm this patient doesn't already exist, to prevent duplicate records.",
    prompt: 'Record the duplicate-patient result.',
    resultValueName: 'duplicate_patient_result',
    requires: IDENTITY_VALUES,
    produces: ['duplicate_patient_result'],
    options: [
      { label: 'No duplicate found', outputs: { duplicate_patient_result: 'no_match' } },
      // A possible duplicate ends the referral: writing the outcome makes the engine
      // cancel every remaining action (finalize_by_decision).
      {
        label: 'Possible duplicate',
        tone: 'danger',
        outputs: { duplicate_patient_result: 'possible_match', final_outcome: 'duplicate' },
      },
    ],
  },
  SexOffenderCheck: {
    label: 'Sex Offender Check',
    category: 'auto_checks',
    description: 'Check federal/state registries for matches to this patient.',
    prompt: 'Record the registry-check result.',
    resultValueName: 'sex_offender_determination',
    requires: POST_IDENTITY_INPUTS,
    produces: ['sex_offender_determination'],
    options: [
      { label: 'No match', outputs: { sex_offender_determination: 'no_match' } },
      { label: 'Possible match', tone: 'danger', outputs: { sex_offender_determination: 'possible_match' } },
    ],
  },
  DenialCheck: {
    label: 'Denial List Check',
    category: 'auto_checks',
    description: "Reference the org's global denial list for matches.",
    prompt: 'Record the denial-list result.',
    resultValueName: 'denial_check_result',
    requires: POST_IDENTITY_INPUTS,
    produces: ['denial_check_result'],
    options: [
      { label: 'Not on denial list', outputs: { denial_check_result: 'clear' } },
      { label: 'On denial list', tone: 'danger', outputs: { denial_check_result: 'match' } },
    ],
  },
  BenefitsEligibility: {
    label: 'Insurance Verification',
    category: 'auto_checks',
    description: 'Verify the patient has active insurance coverage.',
    prompt: 'Record the insurance verification result.',
    resultValueName: 'benefits_eligibility_result',
    requires: POST_IDENTITY_INPUTS,
    produces: ['benefits_eligibility_result'],
    options: [
      { label: 'Coverage active', outputs: { benefits_eligibility_result: 'active' } },
      { label: 'Coverage inactive', tone: 'danger', outputs: { benefits_eligibility_result: 'inactive' } },
    ],
  },
  ClinicalReview: {
    label: 'Review Clinical Assessment',
    category: 'clinical_review',
    description: "Clinical reviewer's determination.",
    prompt: 'Record your clinical determination.',
    resultValueName: 'clinical_determination',
    requires: POST_IDENTITY_INPUTS,
    produces: ['clinical_determination'],
    options: [
      { label: 'Meets criteria', outputs: { clinical_determination: 'meets_criteria' } },
      {
        label: 'Does not meet criteria',
        tone: 'danger',
        outputs: { clinical_determination: 'does_not_meet_criteria' },
      },
    ],
  },
  AcceptReferral: {
    label: 'Referral Determination',
    category: 'decision',
    description: 'The accept/reject decision (writes the outcome).',
    prompt: 'Make the accept or reject decision.',
    resultValueName: 'final_outcome',
    requires: ['clinical_determination'],
    produces: ['final_outcome'],
    options: [
      { label: 'Accept', outputs: { final_outcome: 'accepted' } },
      { label: 'Reject', tone: 'danger', outputs: { final_outcome: 'rejected' } },
    ],
  },
};

export function actionKindMeta(kind: string): ActionKindMeta | undefined {
  return ACTION_KINDS[kind];
}

// The kind that produces a given value name (e.g. `clinical_determination` ->
// `ClinicalReview`), used to explain what a waiting action depends on.
export function producerKindForValue(valueName: string): string | undefined {
  return Object.keys(ACTION_KINDS).find((kind) => ACTION_KINDS[kind].produces.includes(valueName));
}

// A stopgap humanization of a declared (snake_case) value name (`patient_dob` ->
// `Patient dob`), used only when the API hasn't supplied a catalog label.
export function valueLabel(name: string): string {
  const spaced = name.replace(/_/g, ' ').trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

// The display label for a value: the API's catalog label (design P1-5) when present,
// else the humanized name.
export function labelForValue(workflow: Workflow, name: string): string {
  return workflow.valueFor(name)?.label ?? valueLabel(name);
}
